import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { Story } from "../types/story";
import { sound } from "../lib/sound";
import { stardust } from "./Stardust";

/**
 * THE LIBRARY — a small shelf of bedtime books the child picks from on demand.
 *
 * Cover books in a responsive grid (two wide on a phone, up to five across on a
 * laptop — see .library-grid), each reusing the dimensional cover chrome
 * (gold keyline, spine, sheen) at card scale. Tapping a book is the first user
 * gesture, so we unlock the synthesized sound here; a soft page-settle + a single
 * stardust breath answer the tap, then App weaves the chosen cover open (the
 * Incantation) before the story plays. A book the child has finished before
 * wears a soft star — presentational only (see lib/library.ts); it never locks
 * or scores anything.
 *
 * Two ways in, one per input kind, so nothing is hover-only:
 *  - Tapping the book always OPENS it — the phone-first bedtime path, never put
 *    behind an extra step.
 *  - "Peek inside" opens the dust-jacket sheet (pages, minutes, ages, level,
 *    paths to choose, what it was woven from). With a mouse it appears in a soft
 *    veil over the book beside "Read this one"; on a touch-only device it's a
 *    small info seal pressed into the cover.
 *
 * Like everything else, this is pure rendering of data the player already has:
 * titles from the CDN catalog (shown at once) and covers/pages from the shipped
 * story JSON (filled in as each file loads) — no content is generated or fetched
 * at pick time; the stories are pre-loaded by App. The sheet shows dust-jacket
 * facts ONLY: `source` (ai|human) and any other analysis metadata stay invisible,
 * because the whole study depends on the comparison being blind.
 */

/** Resolve a relative asset `src` against THIS story's own base (CDN: per-version). */
function resolveSrc(src: string, assetBase: string): string {
  if (/^https?:\/\//.test(src) || src.startsWith("/")) return src;
  return `${assetBase}${src}`;
}

/**
 * One book on the shelf. `title` comes from the catalog and shows immediately;
 * `story` is null until its JSON loads, then the real cover + page count appear.
 * `assetBase` is that story's OWN asset prefix (each CDN story lives under
 * `<storyId>/v<n>/`), so its cover resolves correctly — null until loaded.
 */
export interface LibraryStoryCard {
  storyId: string;
  title: string;
  assetBase: string | null;
  story: Story | null;
  read: boolean;
}

export function LibraryView({
  childName,
  stories,
  onPick,
}: {
  childName: string;
  stories: LibraryStoryCard[];
  onPick: (storyId: string) => void;
}) {
  const name = (childName || "").trim();
  // Which book is open on the dust-jacket sheet (by storyId; null = none). Held
  // by id, not by object, so the card's progressive fill keeps flowing into it.
  const [peek, setPeek] = useState<string | null>(null);
  const peeked = stories.find((c) => c.storyId === peek);
  // Narrowed for the sheet: there is nothing to show until the JSON has landed.
  const jacket = peeked?.story ? { ...peeked, story: peeked.story } : null;

  return (
    <div className="library">
      <div className="library-head">
        <h1 className="library-title">
          {name ? `${name}’s bedtime library` : "Bedtime library"}
        </h1>
        <p className="library-sub">
          <span className="cover-rule" aria-hidden="true" />
          <StarGlyph className="library-sub__star" />
          <span>pick tonight’s story</span>
          <span className="cover-rule" aria-hidden="true" />
        </p>
      </div>

      <ul className="library-grid">
        {stories.map((card, i) => (
          <li
            key={card.storyId}
            className="library-cell"
            style={{ "--i": i } as CSSProperties}
          >
            <LibraryCard
              card={card}
              onPick={onPick}
              onPeek={() => setPeek(card.storyId)}
            />
          </li>
        ))}
      </ul>

      {jacket && (
        <PreviewSheet card={jacket} onClose={() => setPeek(null)} onPick={onPick} />
      )}
    </div>
  );
}

function LibraryCard({
  card,
  onPick,
  onPeek,
}: {
  card: LibraryStoryCard;
  onPick: (storyId: string) => void;
  onPeek: () => void;
}) {
  const { storyId, title, assetBase, story, read } = card;
  const loading = story === null;
  const cover = story?.cover;
  const pages = pageCount(story);

  const label = loading
    ? `“${title}” is being woven…`
    : `Open “${title}”, a read-along of ${pages} ${
        pages === 1 ? "page" : "pages"
      }${read ? ", already read" : ""}`;

  function open() {
    if (loading) return;
    // The tap is the first user gesture in the library — unlock synthesized
    // sound, then a soft paper settle + one stardust breath as the book opens.
    sound.unlock();
    sound.pageSettle();
    stardust.breathe();
    onPick(storyId);
  }

  return (
    <div className={loading ? "library-card" : "library-card library-card--ready"}>
      <button
        type="button"
        className="library-card__open"
        onPointerDown={() => sound.unlock()}
        onClick={open}
        disabled={loading}
        aria-busy={loading || undefined}
        aria-label={label}
      >
        <span className="lib-book" aria-hidden="true">
          {cover?.src && assetBase != null ? (
            <img
              className="lib-book__face"
              src={resolveSrc(cover.src, assetBase)}
              alt=""
              draggable={false}
              decoding="async"
            />
          ) : (
            // Reuse the cover's dusk-scene placeholder (moon + star scatter) for a
            // book still weaving, or one with no authored cover art.
            <span className="lib-book__face cover-board__face--placeholder" />
          )}
          <span className="lib-book__spine" />
          <span className="lib-book__sheen" />
          <span className="lib-book__keyline" />
          {read && (
            <span className="lib-book__read" title="You’ve read this one">
              <StarGlyph className="lib-book__read-star" />
            </span>
          )}
        </span>

        <span className="library-card__title">{title}</span>
        <span className="library-card__meta">
          {loading ? (
            "weaving…"
          ) : (
            <>
              {pages} {pages === 1 ? "page" : "pages"}
              {read && <span className="library-card__readmark"> · read</span>}
            </>
          )}
        </span>
      </button>

      {!loading && (
        <>
          {/* With a mouse: the two named ways in, in a soft veil over the book.
              Decorative duplicates of paths that already exist (the book itself
              and the info seal), so it stays out of the tab order. */}
          <div className="card-veil" aria-hidden="true">
            <button
              type="button"
              className="card-veil__act"
              onClick={onPeek}
              tabIndex={-1}
            >
              Peek inside
            </button>
            <button
              type="button"
              className="card-veil__act card-veil__act--read"
              onClick={open}
              tabIndex={-1}
            >
              ▶ Read this one
            </button>
          </div>

          {/* The sheet's non-hover route: a small info seal, and the keyboard's
              way in (the veil above is aria-hidden and untabbable). */}
          <button
            type="button"
            className="card-info"
            onClick={onPeek}
            aria-label={`About “${title}”`}
          >
            <InfoGlyph />
          </button>
        </>
      )}
    </div>
  );
}

/* ---- the dust jacket ------------------------------------------------------ */

/**
 * The book's inside cover: what a parent would read on a dust jacket before
 * choosing. Deliberately NOT the story text, and never `source` — only the
 * story's shape (pages, minutes, ages, level, how many paths) and what it was
 * woven from.
 */
function PreviewSheet({
  card,
  onClose,
  onPick,
}: {
  /** Only rendered with a loaded story (see the caller's `peeked?.story` gate). */
  card: LibraryStoryCard & { story: Story };
  onClose: () => void;
  onPick: (storyId: string) => void;
}) {
  const { story } = card;
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pages = pageCount(story);
  const minutes = estimateMinutes(story);
  const choices = choiceCount(story);
  const madeFor = story.child?.name;
  const interests = story.child?.interests ?? [];

  function read() {
    sound.unlock();
    sound.pageSettle();
    stardust.breathe();
    onPick(card.storyId);
  }

  return (
    <div className="preview-scrim" onClick={onClose}>
      <div
        className="preview-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`About ${story.title || card.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className="preview-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="preview-cover" aria-hidden="true">
          {story.cover?.src && card.assetBase != null ? (
            <img
              src={resolveSrc(story.cover.src, card.assetBase)}
              alt=""
              draggable={false}
              decoding="async"
            />
          ) : (
            <span className="lib-book__face cover-board__face--placeholder" />
          )}
        </div>

        <div className="preview-body">
          {card.read && (
            <p className="preview-ribbon">
              <StarGlyph className="preview-ribbon__star" /> You’ve read this one
            </p>
          )}
          {madeFor && (
            <p className="preview-dedication">
              <StarGlyph className="preview-dedication__star" /> Made for {madeFor}
            </p>
          )}
          <h2 className="preview-title">{story.title || card.title}</h2>

          <ul className="preview-chips">
            <Chip>
              {pages} {pages === 1 ? "page" : "pages"}
            </Chip>
            <Chip>about {minutes} min</Chip>
            {story.ageBand && <Chip>ages {story.ageBand}</Chip>}
            {story.readingLevel && <Chip>level {story.readingLevel}</Chip>}
            {choices > 0 && (
              <Chip>
                {choices} {choices === 1 ? "path" : "paths"} to choose
              </Chip>
            )}
            {typeof story.language === "string" && story.language && (
              <Chip>{story.language.toUpperCase()}</Chip>
            )}
          </ul>

          {interests.length > 0 && (
            <p className="preview-loves">
              Woven with the things they love: <em>{interests.join(", ")}</em>
            </p>
          )}

          <button
            type="button"
            className="btn btn--start preview-start"
            onClick={read}
          >
            ▶ Read this story
          </button>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <li className="preview-chip">{children}</li>;
}

/* ---- dust-jacket math ----------------------------------------------------- */

function pageCount(story: Story | null): number {
  if (!story) return 0;
  return Array.isArray(story.canonicalPath) && story.canonicalPath.length > 0
    ? story.canonicalPath.length
    : story.scenes.length;
}

/** Authored estimate when present, else derived from the narration itself. */
function estimateMinutes(story: Story): number {
  if (typeof story.estimatedMinutes === "number" && story.estimatedMinutes > 0) {
    return Math.max(1, Math.round(story.estimatedMinutes));
  }
  const ms = story.scenes.reduce((sum, s) => sum + (s.audio?.durationMs ?? 0), 0);
  return Math.max(1, Math.round(ms / 60000));
}

/** How many scenes offer a real fork. */
function choiceCount(story: Story): number {
  return story.scenes.filter((s) => (s.choice?.options?.length ?? 0) > 1).length;
}

/* ---- glyphs --------------------------------------------------------------- */

/** A small four-point star — header flourish and the "already read" badge. */
function StarGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 17l-5.4 2.8 1.2-6L3.3 9.4l6.1-.8L12 3z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The "about this book" seal on the cover. */
function InfoGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <path d="M12 10.6v6" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      <circle cx="12" cy="7.4" r="1.25" fill="currentColor" />
    </svg>
  );
}
